import { useMemo } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { getContrastText } from "@/lib/avatarColors";
import { cn } from "@/lib/utils";

export type UserAvatarUser = {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  avatar_url?: string | null;
  // Stored brand/custom avatar fill color (hex). When set, the fallback renders
  // as a solid fill of this color; when null/absent it falls back to the
  // deterministic getAvatarPalette pastel.
  color?: string | null;
};

const AVATAR_COLORS = [
  { bg: "#fde8e6", text: "#ef4129" },
  { bg: "#e8f0fe", text: "#1a56b0" },
  { bg: "#e6f4ea", text: "#137333" },
  { bg: "#f3e8fd", text: "#7b1fa2" },
  { bg: "#fff8e1", text: "#b45309" },
  { bg: "#e0f7fa", text: "#00695c" },
];

export function getAvatarPalette(userId: number) {
  return AVATAR_COLORS[userId % AVATAR_COLORS.length];
}

export function getUserInitials(
  user: Pick<UserAvatarUser, "first_name" | "last_name" | "email">
): string {
  const first = user.first_name?.[0] || "";
  const last = user.last_name?.[0] || "";
  // `email` is typed `string`, but the app builds with `strictNullChecks: false`
  // and profiles reach here straight from the DB — a nameless row with a null
  // email would throw on `[0]`, taking down whatever mounted the avatar.
  return (first + last).toUpperCase() || user.email?.[0]?.toUpperCase() || "?";
}

export function getUserDisplayName(
  user: Pick<UserAvatarUser, "first_name" | "last_name" | "email">
): string {
  // Same nameless, null-email row as above: returning that null would reach
  // `alt`/`title` here, and callers persist this string (a picked person's name
  // in `document_forms.values`, the cmdk item value) — so always a string.
  return `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || "";
}

// Resolves either a fully-qualified URL (OAuth-supplied) or a storage path
// in the `avatars` bucket. Returns null when no avatar is set.
export function resolveAvatarUrl(avatarUrl: string | null | undefined): string | null {
  if (!avatarUrl) return null;
  if (avatarUrl.startsWith("http://") || avatarUrl.startsWith("https://")) {
    return avatarUrl;
  }
  const { data } = supabase.storage.from("avatars").getPublicUrl(avatarUrl);
  return data.publicUrl;
}

export interface UserAvatarProps {
  user: UserAvatarUser | null | undefined;
  size?: number;
  className?: string;
  // Optional cache-bust string appended to the resolved URL — used right after
  // an upload so browsers reload the new image. Stored in the caller's local state.
  cacheBust?: string | number | null;
}

export function UserAvatar({ user, size = 24, className, cacheBust }: UserAvatarProps) {
  const src = useMemo(() => {
    const url = resolveAvatarUrl(user?.avatar_url);
    if (!url || !cacheBust) return url;
    return `${url}${url.includes("?") ? "&" : "?"}v=${cacheBust}`;
  }, [user?.avatar_url, cacheBust]);

  if (!user) return null;

  // Solid fill from the user's stored color (initials in a luminance-picked
  // contrast color); otherwise the deterministic pastel fallback.
  const fallbackStyle = user.color
    ? { backgroundColor: user.color, color: getContrastText(user.color) }
    : (() => {
        const palette = getAvatarPalette(user.id);
        return { backgroundColor: palette.bg, color: palette.text };
      })();
  const initials = getUserInitials(user);
  const displayName = getUserDisplayName(user);

  return (
    <Avatar
      className={cn("rounded-full font-semibold", className)}
      style={{ width: size, height: size, fontSize: size * 0.4 }}
      title={displayName}
    >
      {src ? <AvatarImage src={src} alt={displayName} /> : null}
      <AvatarFallback
        className="rounded-full"
        style={{
          ...fallbackStyle,
          fontSize: size * 0.4,
        }}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
